import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard } from "@project/admin/components/module-ui";
import { TablePagination } from "@project/admin/components/table-pagination";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@project/admin/components/searchable-select";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@project/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Badge } from "@project/components/ui/badge";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  UserPlus,
  X,
  Loader2,
  Users,
  UserCheck,
  LogOut,
  HeartOff,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { formatDate } from "@project/lib/date-format";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { residentService } from "@project/services/admin/resident.service";
import { locationService } from "@project/services/admin/location.service";
import { relationshipService } from "@project/services/admin/relationship.service";
import { fetchAllListData } from "@project/services/admin/list-api-helpers";
import type { ApiPagination } from "@project/types/location";
import {
  ADMISSION_TYPE_OPTIONS,
  CATEGORY_OPTIONS,
  GENDER_OPTIONS,
  JAIN_STATUS_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  STATUS_OPTIONS,
  normalizeResidentStatus,
  type ResidentPayload,
  type ResidentRecord,
  type ResidentStats,
} from "@project/types/resident";

const PAGE_SIZE = 20;

const EMPTY_STATS: ResidentStats = {
  total: 0,
  active: 0,
  discharged: 0,
  deceased: 0,
};

type FormState = {
  full_name: string;
  gender: string;
  date_of_birth: string;
  blood_group: string;
  mobile: string;
  aadhaar: string;
  districtId: string;
  cityId: string;
  areaId: string;
  districtName: string;
  cityName: string;
  areaName: string;
  pincode: string;
  religion: string;
  jain_status: string;
  marital_status: string;
  admission_date: string;
  admission_form_no: string;
  admission_type: string;
  monthly_maintenance: string;
  room_no: string;
  category: string;
  status: string;
  previous_address: string;
  reason_for_admission: string;
  notes: string;
};

type FamilyRow = {
  rowId: string;
  id?: number;
  name: string;
  relation: string;
  relationName: string;
  mobile: string;
  alt_mobile: string;
  email: string;
  date_of_birth: string;
  occupation: string;
  districtId: string;
  cityId: string;
  areaId: string;
  districtName: string;
  cityName: string;
  areaName: string;
  address: string;
  is_primary_contact: boolean;
  is_emergency_contact: boolean;
};

const EMPTY_FORM: FormState = {
  full_name: "",
  gender: "",
  date_of_birth: "",
  blood_group: "",
  mobile: "",
  aadhaar: "",
  districtId: "",
  cityId: "",
  areaId: "",
  districtName: "",
  cityName: "",
  areaName: "",
  pincode: "",
  religion: "",
  jain_status: "",
  marital_status: "",
  admission_date: "",
  admission_form_no: "",
  admission_type: "",
  monthly_maintenance: "",
  room_no: "",
  category: "",
  status: "active",
  previous_address: "",
  reason_for_admission: "",
  notes: "",
};

function newFamilyRow(isPrimary = false): FamilyRow {
  return {
    rowId: crypto.randomUUID(),
    name: "",
    relation: "",
    relationName: "",
    mobile: "",
    alt_mobile: "",
    email: "",
    date_of_birth: "",
    occupation: "",
    districtId: "",
    cityId: "",
    areaId: "",
    districtName: "",
    cityName: "",
    areaName: "",
    address: "",
    is_primary_contact: isPrimary,
    is_emergency_contact: isPrimary,
  };
}

function recordToForm(r: ResidentRecord): FormState {
  return {
    full_name: r.full_name ?? "",
    gender: r.gender ?? "",
    date_of_birth: r.date_of_birth ?? "",
    blood_group: r.blood_group ?? "",
    mobile: r.mobile ?? "",
    aadhaar: r.aadhaar ?? "",
    districtId: r.district !== null ? String(r.district) : "",
    cityId: r.city !== null ? String(r.city) : "",
    areaId: r.area !== null ? String(r.area) : "",
    districtName: r.district_name ?? "",
    cityName: r.city_name ?? "",
    areaName: r.area_name ?? "",
    pincode: r.pincode ?? "",
    religion: r.religion ?? "",
    jain_status: r.jain_status ?? "",
    marital_status: r.marital_status ?? "",
    admission_date: r.admission_date ?? "",
    admission_form_no: r.admission_form_no ?? "",
    admission_type: r.admission_type ?? "",
    monthly_maintenance:
      r.monthly_maintenance !== null ? String(r.monthly_maintenance) : "",
    room_no: r.room_no ?? "",
    category: r.category ?? "",
    status: r.status ?? "active",
    previous_address: r.previous_address ?? "",
    reason_for_admission: r.reason_for_admission ?? "",
    notes: r.notes ?? "",
  };
}

function recordToFamilyRows(r: ResidentRecord): FamilyRow[] {
  const members = r.family_members ?? [];
  return members.map(m => ({
    rowId: crypto.randomUUID(),
    id: m.id,
    name: m.name ?? "",
    relation: m.relationship !== null ? String(m.relationship) : "",
    relationName: m.relationship_name ?? "",
    mobile: m.mobile ?? "",
    alt_mobile: m.alt_mobile ?? "",
    email: m.email ?? "",
    date_of_birth: m.date_of_birth ?? "",
    occupation: m.occupation ?? "",
    districtId: m.district !== null ? String(m.district) : "",
    cityId: m.city !== null ? String(m.city) : "",
    areaId: m.area !== null ? String(m.area) : "",
    districtName: m.district_name ?? "",
    cityName: m.city_name ?? "",
    areaName: m.area_name ?? "",
    address: m.address ?? "",
    is_primary_contact: !!m.is_primary_contact,
    is_emergency_contact: !!m.is_emergency_contact,
  }));
}

function trimmed(value: string): string | undefined {
  const v = value.trim();
  return v ? v : undefined;
}

function numericId(value: string): number | undefined {
  return value ? Number(value) : undefined;
}

function buildPayload(form: FormState, family: FamilyRow[]): ResidentPayload {
  const payload: ResidentPayload = {
    full_name: form.full_name.trim(),
    gender: trimmed(form.gender),
    date_of_birth: trimmed(form.date_of_birth),
    blood_group: trimmed(form.blood_group),
    mobile: trimmed(form.mobile),
    aadhaar: trimmed(form.aadhaar),
    district: numericId(form.districtId),
    city: numericId(form.cityId),
    area: numericId(form.areaId),
    pincode: trimmed(form.pincode),
    religion: trimmed(form.religion),
    jain_status: trimmed(form.jain_status),
    marital_status: trimmed(form.marital_status),
    admission_date: trimmed(form.admission_date),
    admission_form_no: trimmed(form.admission_form_no),
    admission_type: trimmed(form.admission_type),
    monthly_maintenance: trimmed(form.monthly_maintenance),
    room_no: trimmed(form.room_no),
    category: trimmed(form.category),
    status: trimmed(form.status),
    previous_address: trimmed(form.previous_address),
    reason_for_admission: trimmed(form.reason_for_admission),
    notes: trimmed(form.notes),
    family_members: family
      .filter(f => f.name.trim())
      .map(f => ({
        ...(f.id ? { id: f.id } : {}),
        name: f.name.trim(),
        relationship: numericId(f.relation),
        mobile: trimmed(f.mobile),
        alt_mobile: trimmed(f.alt_mobile),
        email: trimmed(f.email),
        date_of_birth: trimmed(f.date_of_birth),
        occupation: trimmed(f.occupation),
        district: numericId(f.districtId),
        city: numericId(f.cityId),
        area: numericId(f.areaId),
        address: trimmed(f.address),
        is_primary_contact: f.is_primary_contact,
        is_emergency_contact: f.is_emergency_contact,
      })),
  };
  return payload;
}

function statusBadge(status: ResidentRecord["status"]) {
  const normalized = normalizeResidentStatus(status);
  const map = {
    active: "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15",
    discharged: "bg-sky-500/15 text-sky-700 hover:bg-sky-500/15",
    deceased: "bg-zinc-500/15 text-zinc-700 hover:bg-zinc-500/15",
    inactive: "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15",
  } as const;
  return (
    <Badge className={`capitalize ${map[normalized]}`}>{normalized}</Badge>
  );
}

async function searchDistricts(
  query: string
): Promise<SearchableSelectOption[]> {
  const result = await locationService.listDistrictsPage({
    page: 1,
    page_size: 50,
    search: query || undefined,
  });
  return result.data.map(d => ({ value: String(d.id), label: d.name }));
}

function makeSearchCities(districtId: string) {
  return async (query: string): Promise<SearchableSelectOption[]> => {
    if (!districtId) {
      return [];
    }
    const result = await locationService.listCitiesPage({
      page: 1,
      page_size: 50,
      district: Number(districtId),
      search: query || undefined,
    });
    return result.data.map(c => ({ value: String(c.id), label: c.name }));
  };
}

function makeSearchAreas(cityId: string) {
  return async (query: string): Promise<SearchableSelectOption[]> => {
    if (!cityId) {
      return [];
    }
    const result = await locationService.listAreasPage({
      page: 1,
      page_size: 50,
      city: Number(cityId),
      search: query || undefined,
    });
    return result.data.map(a => ({ value: String(a.id), label: a.name }));
  };
}

async function searchRelationships(
  query: string
): Promise<SearchableSelectOption[]> {
  const result = await relationshipService.listRelationshipsPage({
    page: 1,
    page_size: 50,
    search: query || undefined,
  });
  return result.data
    .filter(r => r.is_active)
    .map(r => ({
      value: String(r.id),
      label: r.relationship_name,
      secondary: r.relationship_code,
    }));
}

function seed(id: string, name: string): SearchableSelectOption | undefined {
  return id && name ? { value: id, label: name } : undefined;
}

export default function Page() {
  const [rows, setRows] = useState<ResidentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<ApiPagination>({
    count: 0,
    current_page: 1,
    total_pages: 1,
    next: null,
    previous: null,
  });
  const [stats, setStats] = useState<ResidentStats>(EMPTY_STATS);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [family, setFamily] = useState<FamilyRow[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ResidentRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [q]);

  const fetchRows = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const result = await residentService.listResidentsPage({
          page: targetPage,
          page_size: PAGE_SIZE,
          search: debouncedQ || undefined,
        });
        setRows(result.data ?? []);
        setPagination(result.pagination);
        setStats(result.stats ?? EMPTY_STATS);
        if (result.pagination.current_page !== targetPage) {
          setPage(result.pagination.current_page);
        }
      } catch (error) {
        projectToast.error(
          getApiErrorMessage(error, "Failed to load residents.")
        );
      } finally {
        setLoading(false);
      }
    },
    [debouncedQ]
  );

  useEffect(() => {
    void fetchRows(page);
  }, [page, fetchRows]);

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const exportRows = await fetchAllListData(
        query => residentService.listResidentsPage(query),
        { search: debouncedQ || undefined }
      );
      if (!exportRows.length) {
        projectToast.error("Nothing to export");
        return;
      }
      exportApiRowsToCsv(
        `residents-${new Date().toISOString().slice(0, 10)}`,
        exportRows as Record<string, unknown>[]
      );
      projectToast.success(`Exported ${exportRows.length} residents`);
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Failed to export CSV"));
    } finally {
      setExportingCsv(false);
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const updateFamily = (rowId: string, patch: Partial<FamilyRow>) => {
    setFamily(prev =>
      prev.map(f => (f.rowId === rowId ? { ...f, ...patch } : f))
    );
  };

  const startNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFamily([newFamilyRow(true)]);
    setOpen(true);
  };

  const startEdit = async (r: ResidentRecord) => {
    setEditingId(r.id);
    setForm(recordToForm(r));
    setFamily([]);
    setOpen(true);
    setFormLoading(true);
    try {
      const full = await residentService.getResident(r.id);
      const record = full ?? r;
      setForm(recordToForm(record));
      setFamily(recordToFamilyRows(record));
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Failed to load resident."));
    } finally {
      setFormLoading(false);
    }
  };

  const closeDialog = (next: boolean) => {
    if (saving) {
      return;
    }
    setOpen(next);
    if (!next) {
      setEditingId(null);
      setForm(EMPTY_FORM);
      setFamily([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) {
      return;
    }
    if (!form.full_name.trim()) {
      projectToast.error("Full name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(form, family);
      if (editingId !== null) {
        await residentService.update(editingId, payload);
        projectToast.success("Resident updated.");
      } else {
        await residentService.create(payload);
        projectToast.success("Resident added.");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setFamily([]);
      void fetchRows(editingId !== null ? page : 1);
      if (editingId === null) {
        setPage(1);
      }
    } catch (error) {
      projectToast.error(getApiErrorMessage(error, "Failed to save resident."));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    try {
      await residentService.delete(deleteTarget.id);
      projectToast.success("Resident deleted.");
      setDeleteTarget(null);
      const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        void fetchRows(page);
      }
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Failed to delete resident.")
      );
    } finally {
      setDeleting(false);
    }
  };

  const emptyMessage = useMemo(
    () =>
      debouncedQ ? "No residents match your search." : "No residents yet.",
    [debouncedQ]
  );

  const addFamily = () =>
    setFamily(prev => [...prev, newFamilyRow(prev.length === 0)]);
  const removeFamily = (rowId: string) =>
    setFamily(prev => prev.filter(f => f.rowId !== rowId));

  return (
    <AdminShell
      title="Residents"
      subtitle="Manage residents — including their family contacts"
    >
      <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Residents"
          value={stats.total}
          icon={<Users className="h-5 w-5" />}
          accent="from-sky-600 to-sky-500"
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<UserCheck className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
        />
        <StatCard
          label="Discharged"
          value={stats.discharged}
          icon={<LogOut className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
        />
        <StatCard
          label="Deceased"
          value={stats.deceased}
          icon={<HeartOff className="h-5 w-5" />}
          accent="from-zinc-600 to-zinc-500"
        />
      </div>

      <div className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, mobile, code, city..."
              value={q}
              onChange={e => setQ(e.target.value)}
              className="bg-muted/40 rounded-xl border-transparent pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <CsvExportButton onExport={exportCsv} exporting={exportingCsv} />
            <Button onClick={startNew} className="gap-1 rounded-xl">
              <Plus className="h-4 w-4" /> Add Resident
            </Button>
          </div>
        </div>

        <div className="-mx-3 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm sm:mx-0">
          <Table className="min-w-[900px]">
            <TableHeader className="bg-muted/60">
              <TableRow>
                <TableHead className="w-16">Photo</TableHead>
                <TableHead>Full Name</TableHead>
                <TableHead>Gender</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Room No</TableHead>
                <TableHead>Admission Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r, i) => (
                  <TableRow
                    key={r.id}
                    className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}
                  >
                    <TableCell>
                      <div className="grid h-10 w-10 place-items-center rounded-full bg-muted text-xs uppercase text-muted-foreground">
                        {(r.full_name ?? "?").slice(0, 1)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{r.full_name}</TableCell>
                    <TableCell className="capitalize">
                      {r.gender ?? "—"}
                    </TableCell>
                    <TableCell>{r.mobile ?? "—"}</TableCell>
                    <TableCell>{r.city_name ?? "—"}</TableCell>
                    <TableCell>{r.room_no ?? "—"}</TableCell>
                    <TableCell>
                      {r.admission_date ? formatDate(r.admission_date) : "—"}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => void startEdit(r)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteTarget(r)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={pagination.count}
          totalPages={pagination.total_pages}
          loading={loading}
          onPageChange={setPage}
        />
      </div>

      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {editingId !== null ? "Edit Resident" : "New Resident"}
            </DialogTitle>
          </DialogHeader>

          {formLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <section className="bg-muted/30 space-y-3 rounded-xl border border-border p-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-primary">
                  Resident Details
                </h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field
                    label="Full Name *"
                    v={form.full_name}
                    onChange={v => setField("full_name", v)}
                    required
                  />
                  <SelectField
                    label="Gender"
                    v={form.gender}
                    onChange={v => setField("gender", v)}
                    options={GENDER_OPTIONS}
                  />
                  <Field
                    label="Date of Birth"
                    type="date"
                    v={form.date_of_birth}
                    onChange={v => setField("date_of_birth", v)}
                  />
                  <Field
                    label="Blood Group"
                    v={form.blood_group}
                    onChange={v => setField("blood_group", v)}
                  />
                  <Field
                    label="Mobile"
                    type="tel"
                    v={form.mobile}
                    onChange={v => setField("mobile", v)}
                  />
                  <Field
                    label="Aadhaar"
                    v={form.aadhaar}
                    onChange={v => setField("aadhaar", v)}
                  />
                  <SelectLike label="District">
                    <SearchableSelect
                      value={form.districtId}
                      onValueChange={v =>
                        setForm(prev => ({
                          ...prev,
                          districtId: v,
                          cityId: "",
                          areaId: "",
                          cityName: "",
                          areaName: "",
                        }))
                      }
                      onSearch={searchDistricts}
                      seedOption={seed(form.districtId, form.districtName)}
                      placeholder="Select district..."
                      searchPlaceholder="Search district..."
                      emptyText="No districts found."
                    />
                  </SelectLike>
                  <SelectLike label="City">
                    <SearchableSelect
                      value={form.cityId}
                      onValueChange={v =>
                        setForm(prev => ({
                          ...prev,
                          cityId: v,
                          areaId: "",
                          areaName: "",
                        }))
                      }
                      onSearch={makeSearchCities(form.districtId)}
                      seedOption={seed(form.cityId, form.cityName)}
                      placeholder={
                        form.districtId
                          ? "Select city..."
                          : "Select district first"
                      }
                      searchPlaceholder="Search city..."
                      emptyText="No cities found."
                      disabled={!form.districtId}
                    />
                  </SelectLike>
                  <SelectLike label="Area">
                    <SearchableSelect
                      value={form.areaId}
                      onValueChange={v => setField("areaId", v)}
                      onSearch={makeSearchAreas(form.cityId)}
                      seedOption={seed(form.areaId, form.areaName)}
                      placeholder={
                        form.cityId ? "Select area..." : "Select city first"
                      }
                      searchPlaceholder="Search area..."
                      emptyText="No areas found."
                      disabled={!form.cityId}
                    />
                  </SelectLike>
                  <Field
                    label="Pincode"
                    v={form.pincode}
                    onChange={v => setField("pincode", v)}
                  />
                  <Field
                    label="Religion"
                    v={form.religion}
                    onChange={v => setField("religion", v)}
                  />
                  <SelectField
                    label="Jain Status"
                    v={form.jain_status}
                    onChange={v => setField("jain_status", v)}
                    options={JAIN_STATUS_OPTIONS}
                  />
                  <SelectField
                    label="Marital Status"
                    v={form.marital_status}
                    onChange={v => setField("marital_status", v)}
                    options={MARITAL_STATUS_OPTIONS}
                  />
                  <Field
                    label="Admission Date"
                    type="date"
                    v={form.admission_date}
                    onChange={v => setField("admission_date", v)}
                  />
                  <Field
                    label="Admission Form No"
                    v={form.admission_form_no}
                    onChange={v => setField("admission_form_no", v)}
                  />
                  <SelectField
                    label="Admission Type"
                    v={form.admission_type}
                    onChange={v => setField("admission_type", v)}
                    options={ADMISSION_TYPE_OPTIONS}
                  />
                  <Field
                    label="Monthly Maintenance (₹)"
                    type="number"
                    v={form.monthly_maintenance}
                    onChange={v => setField("monthly_maintenance", v)}
                  />
                  <Field
                    label="Room No"
                    v={form.room_no}
                    onChange={v => setField("room_no", v)}
                  />
                  <SelectField
                    label="Category"
                    v={form.category}
                    onChange={v => setField("category", v)}
                    options={CATEGORY_OPTIONS}
                  />
                  <SelectField
                    label="Status"
                    v={form.status}
                    onChange={v => setField("status", v)}
                    options={STATUS_OPTIONS}
                  />
                </div>
                <div>
                  <Label>Previous Address</Label>
                  <Textarea
                    rows={2}
                    value={form.previous_address}
                    onChange={e => setField("previous_address", e.target.value)}
                    className="rounded-xl border-input bg-background"
                  />
                </div>
                <div>
                  <Label>Reason for Admission</Label>
                  <Textarea
                    rows={2}
                    value={form.reason_for_admission}
                    onChange={e =>
                      setField("reason_for_admission", e.target.value)
                    }
                    className="rounded-xl border-input bg-background"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    rows={2}
                    value={form.notes}
                    onChange={e => setField("notes", e.target.value)}
                    className="rounded-xl border-input bg-background"
                  />
                </div>
              </section>

              <section className="bg-muted/30 space-y-3 rounded-xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-primary">
                    Family Members ({family.length})
                  </h3>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addFamily}
                    className="gap-1"
                  >
                    <UserPlus className="h-3 w-3" /> Add Family Member
                  </Button>
                </div>
                {family.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No family contacts added yet.
                  </p>
                ) : (
                  family.map(f => (
                    <div
                      key={f.rowId}
                      className="relative space-y-3 rounded-lg border border-border bg-background p-4"
                    >
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="absolute right-1 top-1 h-7 w-7 text-destructive"
                        onClick={() => removeFamily(f.rowId)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="flex flex-wrap gap-3 text-xs">
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={f.is_primary_contact}
                            onChange={e =>
                              updateFamily(f.rowId, {
                                is_primary_contact: e.target.checked,
                              })
                            }
                          />{" "}
                          Primary contact
                        </label>
                        <label className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={f.is_emergency_contact}
                            onChange={e =>
                              updateFamily(f.rowId, {
                                is_emergency_contact: e.target.checked,
                              })
                            }
                          />{" "}
                          Emergency
                        </label>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <Field
                          label="Name *"
                          v={f.name}
                          onChange={v => updateFamily(f.rowId, { name: v })}
                        />
                        <SelectLike label="Relation">
                          <SearchableSelect
                            value={f.relation}
                            onValueChange={v =>
                              updateFamily(f.rowId, { relation: v })
                            }
                            onSearch={searchRelationships}
                            seedOption={seed(f.relation, f.relationName)}
                            placeholder="Select relation..."
                            searchPlaceholder="Search relation..."
                            emptyText="No relationship found."
                          />
                        </SelectLike>
                        <Field
                          label="Mobile"
                          type="tel"
                          v={f.mobile}
                          onChange={v => updateFamily(f.rowId, { mobile: v })}
                        />
                        <Field
                          label="Alt Mobile"
                          type="tel"
                          v={f.alt_mobile}
                          onChange={v =>
                            updateFamily(f.rowId, { alt_mobile: v })
                          }
                        />
                        <Field
                          label="Email"
                          type="email"
                          v={f.email}
                          onChange={v => updateFamily(f.rowId, { email: v })}
                        />
                        <Field
                          label="Date of Birth"
                          type="date"
                          v={f.date_of_birth}
                          onChange={v =>
                            updateFamily(f.rowId, { date_of_birth: v })
                          }
                        />
                        <Field
                          label="Occupation"
                          v={f.occupation}
                          onChange={v =>
                            updateFamily(f.rowId, { occupation: v })
                          }
                        />
                        <SelectLike label="District">
                          <SearchableSelect
                            value={f.districtId}
                            onValueChange={v =>
                              updateFamily(f.rowId, {
                                districtId: v,
                                cityId: "",
                                areaId: "",
                                cityName: "",
                                areaName: "",
                              })
                            }
                            onSearch={searchDistricts}
                            seedOption={seed(f.districtId, f.districtName)}
                            placeholder="Select district..."
                            searchPlaceholder="Search district..."
                            emptyText="No districts found."
                          />
                        </SelectLike>
                        <SelectLike label="City">
                          <SearchableSelect
                            value={f.cityId}
                            onValueChange={v =>
                              updateFamily(f.rowId, {
                                cityId: v,
                                areaId: "",
                                areaName: "",
                              })
                            }
                            onSearch={makeSearchCities(f.districtId)}
                            seedOption={seed(f.cityId, f.cityName)}
                            placeholder={
                              f.districtId
                                ? "Select city..."
                                : "Select district first"
                            }
                            searchPlaceholder="Search city..."
                            emptyText="No cities found."
                            disabled={!f.districtId}
                          />
                        </SelectLike>
                        <SelectLike label="Area">
                          <SearchableSelect
                            value={f.areaId}
                            onValueChange={v =>
                              updateFamily(f.rowId, { areaId: v })
                            }
                            onSearch={makeSearchAreas(f.cityId)}
                            seedOption={seed(f.areaId, f.areaName)}
                            placeholder={
                              f.cityId ? "Select area..." : "Select city first"
                            }
                            searchPlaceholder="Search area..."
                            emptyText="No areas found."
                            disabled={!f.cityId}
                          />
                        </SelectLike>
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Textarea
                          rows={2}
                          value={f.address}
                          onChange={e =>
                            updateFamily(f.rowId, { address: e.target.value })
                          }
                          className="bg-muted/40 rounded-xl border-transparent"
                        />
                      </div>
                    </div>
                  ))
                )}
              </section>

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full rounded-xl sm:w-auto"
                >
                  {saving
                    ? "Saving…"
                    : editingId !== null
                      ? "Update Resident"
                      : "Save Resident"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={open => {
          if (!open && !deleting) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete resident?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <span className="font-medium text-foreground">
                {deleteTarget?.full_name}
              </span>{" "}
              and their family records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="rounded-xl">
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={deleting}
              className="hover:bg-destructive/90 rounded-xl bg-destructive text-destructive-foreground"
              onClick={() => void confirmDelete()}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}

function Field({
  label,
  v,
  onChange,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  v: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type={type}
        required={required}
        value={v ?? ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xl border-input bg-background"
      />
    </div>
  );
}

function SelectLike({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  v,
  onChange,
  options,
}: {
  label: string;
  v: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={v || undefined} onValueChange={onChange}>
        <SelectTrigger className="rounded-xl border-input bg-background">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
